import { Ref, computed, inject, provide } from "vue";
import { Button, Brand, AdTag } from "@fresh8/types/src/index";

export function createBrand(brand: Ref<Brand>) {
  const adTemplate = computed(() => brand.value?.adTemplate);
  const buttons = computed(() => brand.value?.buttons);
  const fonts = computed(() => brand.value?.fonts);
  const colors = computed(() => brand.value?.colors);
  const marketBranding = computed(() => brand.value?.adTemplate.market);
  const footerBranding = computed(() => brand.value?.adTemplate.footer);
  const headerBranding = computed(() => brand.value?.adTemplate.header);
  const operatorLogos = computed(() => brand.value?.operatorLogos);
  const offers = computed(() => brand.value?.offers);
  const backgroundImageSets = computed(() => brand.value.backgroundImageSets);
  const potentialWinnings = computed(
    () => brand.value?.adTemplate.potentialWinnings
  );
  const oddsDisplayTemplate = computed(
    () => marketBranding.value.oddsButtonTemplate
  );

  const operatorHasOffer = (operatorId: string) => {
    const operatorOffer = offers.value?.[operatorId];

    return (
      operatorOffer &&
      ((operatorOffer.body.length || 0) > 0 ||
        (operatorOffer.header.length || 0) > 0 ||
        (operatorOffer.buttonText.length || 0) > 0 ||
        (operatorOffer.terms.length || 0) > 0)
    );
  };

  const getColor = (colorKey: string) => colors.value?.[colorKey]?.color;

  const adTemplateBranding = computed(() => {
    return {
      backgroundColor: getColor(adTemplate.value?.backgroundColor),
      borderRadius: adTemplate.value?.borderRadius,
      marketBorderColor:
        getColor(adTemplate.value?.marketBorderColor) || "#dcddf"
    };
  });

  const teamBranding = computed(() => ({
    banner: {
      font: Object.keys(fonts.value).includes(
        adTemplate.value?.team?.banner?.font
      )
        ? adTemplate.value?.team?.banner?.font
        : Object.keys(fonts.value)[0],
      backgroundColor:
        getColor(adTemplate.value?.team?.banner?.backgroundColor) ||
        getColor(Object.keys(colors.value)[0])
    },
    badge: {
      borderColor:
        getColor(adTemplate.value?.team?.badge?.borderColor) ||
        getColor(Object.keys(colors.value)[0])
    },
    versusIcon: {
      borderColor:
        getColor(adTemplate.value?.team?.versusIcon?.borderColor) || "#717171",
      backgroundColor:
        getColor(adTemplate.value?.team?.versusIcon?.borderColor) || "#fff"
    }
  }));

  const getColorStyle = (colorId: string) => colors.value?.[colorId]?.color;

  const getFontStyle = (fontId: string, fallback?: boolean) => {
    let font = fonts.value[fontId];

    if (fallback && !font) {
      font = fonts.value[Object.keys(fonts.value)[0]];
    }

    if (!font) return undefined;

    const { fontAsset, ...fontRest } = font;

    return {
      ...fontRest,
      color: getColorStyle(font.color),
      fontFamily: fontAsset?.fontFamily
    };
  };

  function getButtonStateColors(action) {
    return {
      primaryColor: getColorStyle(action.primaryColor),
      secondaryColor: getColorStyle(action.secondaryColor),
      backgroundColor: getColorStyle(action.backgroundColor),
      borderColor: getColorStyle(action.borderColor)
    };
  }

  const getButtonStyle = (buttonId: string, fallback?: boolean) => {
    let button: Button = buttons.value[buttonId];
    const defaultButton = Object.keys(buttons.value)[0];

    if (fallback && !button && defaultButton) {
      button = buttons.value[defaultButton];
    } else if (fallback && !button && !defaultButton) {
      button = {
        name: "",
        type: "odds",
        primaryFont: "",
        secondaryFont: "",
        autoInteractions: true,
        iconColor: "",
        actions: {}
      };
    }

    return {
      ...button,
      iconColor: getColorStyle(button?.iconColor || "#000"),
      backgroundColor: getColorStyle(button?.backgroundColor || ""),
      borderColor: getColorStyle(button?.borderColor || ""),
      actions: {
        hover: button?.actions.hover
          ? getButtonStateColors(button.actions.hover)
          : undefined,
        active: button?.actions.active
          ? getButtonStateColors(button.actions.active)
          : undefined
      }
    };
  };

  return {
    adTemplate,
    brand,
    fonts,
    buttons,
    offers,
    teamBranding,
    adTemplateBranding,
    headerBranding,
    marketBranding,
    footerBranding,
    operatorLogos,
    backgroundImageSets,
    potentialWinnings,
    getColorStyle,
    getFontStyle,
    getButtonStyle,
    operatorHasOffer,
    oddsDisplayTemplate
  };
}

export type BrandContext = ReturnType<typeof createBrand>;
export type FontStyle = ReturnType<BrandContext["getFontStyle"]>;
export type ButtonStyle = ReturnType<BrandContext["getButtonStyle"]>;

export const brandSymbol = Symbol("brandSymbol");

export function provideBrand(brand: Ref<Brand>) {
  provide(brandSymbol, createBrand(brand));
}

export function injectBrand() {
  return inject<BrandContext>(brandSymbol);
}

export function createAdTag(adTag: Ref<AdTag>) {
  const responsive = computed(() => {
    if (adTag.value.size.responsive) {
      return adTag.value.size.responsive;
    }

    return adTag.value?.responsive;
  });

  const adTagSize = computed(() => adTag.value?.size);
  const operators = computed(() => adTag.value?.operators);

  const randomOperator = computed(() => {
    if (operators.value?.length > 0) {
      const randowSelection = Math.floor(
        Math.random() * operators.value.length
      );
      return operators.value[randowSelection];
    }
    return adTag.value?.operator;
  });

  const operator = computed(() => {
    if (adTag.value?.operator) {
      return adTag.value?.operator;
    }

    return randomOperator.value;
  });

  return {
    responsive,
    adTagSize,
    adTag,
    operator,
    operators,
    randomOperator
  };
}

export type AdTagContext = ReturnType<typeof createAdTag>;
export const adTagSymbol = Symbol("AdTagSymbol");

export function provideAdTag(adTag: Ref<AdTag>) {
  provide(adTagSymbol, createAdTag(adTag));
}

export function injectAdTag() {
  return inject<AdTagContext>(adTagSymbol);
}
